import { ResolverInterface, ResolverWithCron } from "@/utils/graph/GraphInterface";
import { Routes } from "@/utils/routes";
import { transformWindowedName } from "@/utils/windowed.utils";
import { first, last } from "lodash";
import { routeForFeature } from "@/utils/feature";

export enum ResolverSpecialSequence {
  ALL = "__all__",
  DOT = "_dot_",
  OFFLINE = "_offline$",
}
export const resolverFqnIsFeatureUnderscore = (fqn: string) =>
  fqn.startsWith("__chalk_underscore__");

export const fqnIsChalkInternal = (fqn: string) => fqn.startsWith("__chalk__");

export const featureUnderscoreToFeatureFqn = (resolverFqn: string): string | null => {
  if (!resolverFqnIsFeatureUnderscore(resolverFqn)) {
    return null;
  }

  return fqnToName(resolverFqn).replace("_dot_", ".");
};

export interface RouteForResolverArgs {
  projectId: string;
  environmentId: string;
  // Short name is dangerous - difficult to distinguish underscore FQNs vs. real resolvers
  resolverFqn: string;
  activeDeploymentId: string | undefined;
  branchName: string | undefined;
  deploymentId: string | undefined;
  tab?: string;
}

export function routeForResolver({
  projectId,
  environmentId,
  resolverFqn,
  activeDeploymentId,
  branchName,
  deploymentId,
}: RouteForResolverArgs): string {
  const resolverShortName = fqnToName(resolverFqn);

  const featureName = featureUnderscoreToFeatureFqn(resolverFqn);

  if (featureName) {
    return routeForFeature({
      projectId,
      environmentId,
      deploymentId,
      activeDeploymentId,
      branchName,
      featureFQN: featureName,
    });
  }

  if (deploymentId == null || deploymentId === activeDeploymentId) {
    return Routes.project.environment.resolver(projectId, environmentId, resolverShortName);
  }
  if (branchName != null) {
    return Routes.project.environment.branchDeploymentResolver(
      projectId,
      environmentId,
      branchName,
      deploymentId,
      resolverShortName,
    );
  }
  return Routes.project.environment.deploymentResolver(
    projectId,
    environmentId,
    deploymentId,
    resolverShortName,
  );
}

/**
 * Use this function ONLY for displaying a prettified version of the resolver name in the UI.
 * Do not use it for any other purpose, e.g. generating links.
 */
export function getResolverPrettyDisplayName(resolver: ResolverInterface): string {
  return getResolverPrettyDisplayNameFromFqn(resolver.fqn);
}

/**
 * Use this function ONLY for displaying a prettified version of the resolver name in the UI.
 * Do not use it for any other purpose, e.g. generating links.
 */
export function getResolverPrettyDisplayNameFromFqn(resolverFqn: string) {
  const transformed = transformWindowedResolverName(fqnToName(resolverFqn));
  // fqn=f"__chalk__embedding__resolver__namespace__{output_feature.namespace}__name__{output_feature.name}",
  if (
    transformed.startsWith("__chalk__embedding__resolver__namespace__") &&
    transformed.split("__").length > 7
  ) {
    // extract the namespace and name of the output feature
    const parts = transformed.split("__");
    const namespace = parts[5];
    const name = parts[7];

    return `${namespace}.${name}`;
  }
  return transformed;
}

export function getResolverName(resolver: ResolverInterface): string {
  return getResolverNameFromFqn(resolver.fqn);
}

export function getResolverNameFromFqn(resolverFqn: string) {
  return fqnToName(resolverFqn);
}

// Creates a short name for the fqn, but is not necessarily the best to display.
// Use getResolverNameFromFqn when displaying in UI
export function fqnToName(fqn: string) {
  if (fqn.includes(".")) {
    return last(fqn.split(".")) ?? "";
  } else {
    // FIXME: this is a stopgap that will handle the changeover
    // when we stop using `foo.bar.baz.banana` for resolvers and
    // start using just `banana` as the fully qualified name
    return fqn;
  }
}

export function fqnToNamespace(fqn: string) {
  return fqn.includes(".") ? fqn.split(".").slice(0, -1).join(".") ?? "" : "";
}

export function pathToFirstElement(fqn: string) {
  return fqn.includes(".") ? first(fqn.split(".")) ?? fqn : fqn;
}

export function fqnToShortNamespace(fqn: string) {
  return (
    fqn
      .split(".")
      .slice(0, -1)
      .map((part) =>
        part
          .split("_")
          .map((word) => word.slice(0, 1))
          .join(""),
      )
      .join(".") ?? ""
  );
}

export function transformWindowedResolverName(fqn: string) {
  const fqnWithWindowStyling = transformWindowedName(fqn, false);

  return fqnWithWindowStyling.replaceAll(ResolverSpecialSequence.DOT, ".");
}

export function resolverHasCron(r: ResolverInterface): r is ResolverWithCron {
  // Not all resolvers with cron have a cron
  // but all have a crontab
  return r.crontab != null;
}

export function getCronResolverDisplaySchedule(r: ResolverWithCron): string {
  // crontab is always there
  // but r.cron will match more closely to the code defined schedule
  return r.cron || r.crontab;
}
